import React, { useState, useContext } from "react";
import { Button, Modal, Steps, Form, Result, Space } from "antd";
import { useStepsForm } from "sunflower-antd";
import type { SelectProps } from "antd";

import { ClaimContext } from "../../../context/ClaimContext";

import Details from "../../FormInputs/Claim/Details";
import Documents from "../../FormInputs/Claim/Documents";

const { Step } = Steps;

interface IModalProps {
  show: boolean;
  claim: any;
  showModal: Function;
  handleUpdate: Function;
}

let options: SelectProps["options"] = [];

const EditClaimModal: React.FC<IModalProps> = ({
  show,
  claim,
  showModal,
  handleUpdate,
}) => {
  const { claimData, resetClaimData } = useContext(ClaimContext);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleModal = () => {
    showModal();
    gotoStep(0);
    resetClaimData();
  };

  const claimType: "Internal" | "External" = claim?.IsExternal ? "External" : "Internal";

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    defaultFormValues: claim,
    async submit(values: any) {
      setLoading(true);
      const { id: DeceasedId, Documents } = claimData;

      const dod =
        values?.DateOfDeath && typeof values.DateOfDeath?.format === "function"
          ? values.DateOfDeath.format("YYYY-MM-DD")
          : values?.DateOfDeath;

      // Whitelist fields per claim type so the PATCH body matches the API contract.
      const base: any = {
        DateOfDeath: dod,
        CauseOfDeath: values.CauseOfDeath,
        Notes: values.Notes ?? "",
        Documents: Documents ?? [],
        DeceasedId,
      };
      const payload =
        claimType === "External"
          ? {
              ...base,
              DeceasedName: values.DeceasedName,
              DeceasedSurname: values.DeceasedSurname,
              DeceasedIdNumber: values.DeceasedIdNumber,
              DeceasedType: values.DeceasedType,
              BenName: values.BenName,
              BenSurname: values.BenSurname,
              BenIdNumber: values.BenIdNumber,
              ClaimAmount: values.ClaimAmount != null ? String(values.ClaimAmount) : undefined,
              AdditionalNotes: values.Notes ?? "",
              Bank: values.Bank,
              AccountNumber: values.AccountNumber,
              AccountType: values.AccountType,
              BranchCode: values.BranchCode,
            }
          : base;

      console.log("[EditClaim] claimType:", claimType, "payload keys:", Object.keys(payload), "Documents count:", payload.Documents?.length);
      const response = await handleUpdate(claim.id, payload);

      if (response.success) {
        resetClaimData();
        setLoading(false);
        return true;
      }

      resetClaimData();
      setErrorMsg(response.message);
      setLoading(false);
      return false;
    },
    total: 11, // Reduced by 1 since we removed a step
  });

  const formList = [
    <Details gotoStep={gotoStep} current={current} claimType={claimType} />,

    <Documents gotoStep={gotoStep} current={current} submit={submit} />,
  ];

  return (
    <Modal
      title="Edit Claim"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={1050}
      footer={null}
    >
      {/* Steps Form */}
      <div style={{ display: "flex", gap: 24 }}>
        {current < formList.length && (
          <div style={{ flexShrink: 0, borderRight: "1px solid #f0f0f0", paddingRight: 16, whiteSpace: "nowrap" }}>
            <Steps {...stepsProps} direction="vertical">
              <Step title="Details" />
              <Step title="Documents" />
            </Steps>
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0, marginTop: 24, overflowX: "auto" }}>
          <Form {...formProps} style={{ width: "100%" }}>
            {formList[current]}
          </Form>

          {current === 8 && (
            <Result
              status="success"
              title="Successfully submitted."
              subTitle="The system administrator will be notified for approval."
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  loading={loading}
                  block
                  size="large"
                  onClick={handleModal}
                >
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 9 && (
            <Result status="error" title={errorMsg}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EditClaimModal;
